import Papa from 'papaparse';
import React, { useEffect, useState } from 'react';

interface ExtendedDocument {
  uri: string;
  viewer?: 'google' | 'office' | 'pdf';
  styles?: React.CSSProperties;
  /**
   * When true, the CSV table fills its parent container (`w-full h-full`)
   * with a white background, instead of being capped at the default 500px
   * max-height. Opt in from layouts that already provide a bounded height
   * (e.g. fullscreen previews); leave off for inline modal previews that
   * rely on the 500px cap to size themselves.
   */
  fillContainer?: boolean;
}

const DocViewerComponent = ({
  uri = '',
  styles,
  fillContainer = false,
}: ExtendedDocument) => {
  // Add CSV to your recognized file types
  const imageExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tiff',
    'ico',
    'webp',
  ];

  const getFileType = (url: string) => {
    const lowerUrl = url.toLowerCase();
    const extension = lowerUrl.split('.').pop()?.split('?')[0] || '';

    if (lowerUrl.includes('.pdf')) return 'pdf';
    if (lowerUrl.includes('.docx')) return 'docx';
    if (lowerUrl.includes('.doc')) return 'doc';
    if (lowerUrl.includes('.xlsx')) return 'xlsx';
    if (lowerUrl.includes('.csv')) return 'csv';

    if (imageExtensions.includes(extension)) return 'image';

    return 'unknown';
  };

  const type = getFileType(uri);

  const [csvData, setCsvData] = useState<string[][]>([]);

  useEffect(() => {
    const fetchCsvData = async () => {
      if (type === 'csv') {
        try {
          const response = await fetch(uri);
          if (!response.ok) {
            throw new Error(
              `Network response was not ok. Status: ${response.status}`
            );
          }
          const text = await response.text();
          const result = Papa.parse<string[]>(text, {
            skipEmptyLines: true,
          });
          // result.data is an array of arrays, each representing a row
          if (result.data && Array.isArray(result.data)) {
            setCsvData(result.data);
          }
        } catch (error) {
          console.error('Error fetching or parsing CSV:', error);
        }
      }
    };

    fetchCsvData();
  }, [uri, type]);

  if (type === 'pdf') {
    return (
      <iframe
        src={uri}
        className="w-full border-0 rounded-md h-full"
        title="PDF Document"
        style={styles}
      />
    );
  }

  if (type === 'image') {
    return (
      <img
        src={uri}
        className="w-full border-0 rounded-md"
        alt="Display Image"
        style={styles}
      />
    );
  }

  if (type === 'csv') {
    // Render CSV data in a table
    return (
      <div
        className={
          fillContainer
            ? 'w-full h-full overflow-auto bg-bg-primary'
            : 'overflow-auto'
        }
        style={fillContainer ? styles : { maxHeight: 500, ...styles }}
      >
        <table className="min-w-full text-left">
          <thead>
            {/* Assuming first row is the header row */}
            {csvData.length > 0 && (
              <tr>
                {csvData[0].map((headerCell) => (
                  <th key={headerCell} className="p-2 border">
                    {headerCell}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {csvData.slice(1).map((row) => (
              <tr key={row[0]}>
                {row.map((cell) => (
                  <td key={cell} className="p-2 border">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback: For Word, Excel or other Office docs, use the MS Office viewer
  const msViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(uri)}`;
  return (
    <iframe
      src={msViewerUrl}
      className="w-full h-full border-0"
      title="Office Document"
      style={styles}
    />
  );
};

export { DocViewerComponent };
